import type { ReactNode } from "react";
import { useEffect, useRef } from "react";

import { useDeviceBreakpoint } from "@ui/hooks/useDeviceBreakpoint";
import { useUser } from "@ui/hooks/useUser";
import { useAuthHrefResolver } from "@ui/providers/AuthRoutingProvider";
import classNames from "classnames";

import { Button } from "@jti/core-components/components/Button";
import { ImageBehaviour } from "@jti/core-components/components/ImageBehaviour";
import { AUTH_ROUTE_PATHS } from "@jti/fe-common/routes/auth-routes";

import type { RichTextPropsContent } from "../../eva-components/RichText";
import { RichText } from "../../eva-components/RichText";
import type { DialogProps } from "../Dialog";
import { BACKGROUND_OPTIONS, Dialog } from "../Dialog";
import type { Language } from "../LanguageSelector";
import { LanguageSelector } from "../LanguageSelector";
import type { ResponsiveImageProps } from "../ResponsiveImage";
import { ResponsiveImage } from "../ResponsiveImage";
import { Typography } from "../Typography";

import type { SocialMediaLink } from "./components/SocialLinks";
import { SocialLinks } from "./components/SocialLinks";

import styles from "./WelcomePopup.module.scss";

export type WelcomePopupProps = {
  externalContent?: ReactNode;
  logo: {
    src: string;
    alt?: string;
  };
  title: string;
  paragraph: RichTextPropsContent;
  cta: {
    text: string;
    secondaryText?: RichTextPropsContent;
  };
  legalNotice: RichTextPropsContent;
  image: ResponsiveImageProps;
  languages?: Language[];
  socialMediaLinks?: SocialMediaLink[];
  socialMediaText?: string;
  isAuthorMode?: boolean;
  backgroundColor?: keyof typeof BACKGROUND_OPTIONS;
  onOpenChange?: (isOpen: boolean) => void;
} & Pick<DialogProps, "portalDomNode">;

export const WelcomePopup = ({
  portalDomNode = document.body,
  externalContent,
  image,
  logo,
  title,
  paragraph,
  cta,
  legalNotice,
  languages,
  socialMediaLinks = [],
  socialMediaText = undefined,
  isAuthorMode,
  backgroundColor = "blurredWhite",
  onOpenChange,
}: WelcomePopupProps) => {
  const resolveAuthHref = useAuthHrefResolver();
  const authEntryHref = resolveAuthHref({ route: AUTH_ROUTE_PATHS.entry });

  const { isUserLoggedIn, user } = useUser();

  const { breakpoint } = useDeviceBreakpoint();
  const onOpenChangeRef = useRef(onOpenChange);

  const showModal = !isUserLoggedIn || (isUserLoggedIn && !user?.is_age_verified);

  useEffect(() => {
    onOpenChangeRef.current = onOpenChange;
  }, [onOpenChange]);

  // Notify parent only when the popup is actually visible.
  useEffect(() => {
    if (isAuthorMode || !showModal) return;
    onOpenChangeRef.current?.(true);
    return () => {
      onOpenChangeRef.current?.(false);
    };
  }, [isAuthorMode, showModal]);

  if (!showModal) {
    return null;
  }

  return (
    <Dialog
      dataTestId="welcome-popup"
      externalContent={externalContent}
      closable={false}
      isOpened={true}
      portalDomNode={portalDomNode}
      fullscreen={breakpoint === "mobile"}
      backgroundColor={BACKGROUND_OPTIONS[backgroundColor].color}
      backgroundBlur={BACKGROUND_OPTIONS[backgroundColor].blur}
      className={classNames(styles.dialog, styles.welcomePopupDialog)}
      isUnwrapped={isAuthorMode}
      withContentPadding={false}
    >
      <div className={styles.welcomePopupWrapper}>
        <div className={styles.welcomePopupContentWrapper}>
          <div className={styles.welcomePopupLanguageSelectorWrapper}>
            <LanguageSelector
              popupContext="AGE_CONFIRMATION"
              variant={breakpoint === "mobile" ? "FULL_LANGUAGE_NAME" : "SHORT_LANGUAGE_NAME"}
              languages={languages || []}
              size="compact"
            />
          </div>
          <div className={styles.welcomePopupContent}>
            <div className={styles.welcomePopupContentTextSection}>
              <div className={styles.welcomePopupLogoWrapper}>
                <ImageBehaviour src={logo.src} alt={logo.alt} className={styles.welcomePopupLogo} />
              </div>
              <Typography className={styles.welcomePopupTitle} variant="h2">
                {title}
              </Typography>
              <RichText className={styles.welcomePopupParagraph} {...paragraph} />
              <div className={styles.welcomePopupCtasWrapper}>
                <Button
                  className={styles.mobileWelcomePopupCta}
                  size="sm"
                  variant="primary"
                  role="button"
                  href={authEntryHref}
                >
                  {cta.text}
                </Button>
                <Button className={styles.desktopWelcomePopupCta} variant="primary" role="button" href={authEntryHref}>
                  {cta.text}
                </Button>
              </div>
              {cta.secondaryText && <RichText className={styles.welcomePopupCtaSecondaryText} {...cta.secondaryText} />}
            </div>
            {socialMediaLinks.length > 0 && (
              <div className={styles.socials}>
                <SocialLinks links={socialMediaLinks} />
                {socialMediaText && <p className={styles.socialText}>{socialMediaText}</p>}
              </div>
            )}
            <RichText className={styles.welcomePopupLegalNotice} {...legalNotice} />
          </div>
        </div>
        <div className={styles.welcomePopupImageContainer}>
          <ResponsiveImage className={styles.welcomePopupImage} {...image} />
        </div>
      </div>
    </Dialog>
  );
};
